import { skipToken } from '@reduxjs/toolkit/query';
import {
  applyBlotterSpaceInfluence,
  cleanupBlotterSpaceInfluence,
  getSpaceInfluenceRowClassRules,
  SpaceInfluenceType,
  subscribeToSpaceInfluenceUpdates,
  useApplySpaceInfluence,
  type SpaceInfluence,
} from '@src/components/AppLayout/SpaceInfluence';
import {
  useOptionalSpaceParameters,
  useSpaceParameterControlledFilter,
  type SpaceParameterCompatibleFilterableProperties,
  type SpaceParameterPanelValueDefinitions,
} from '@src/components/AppLayout/SpaceParameters';
import type { EntityAdminBlotterTableApi, EntityAdminRecord, FilterableProperty } from '@talos/kyoko';
import { useCallback, useMemo, useState } from 'react';
import { useGetCustomerConfigurationQuery } from '../customerConfigurationApi';
import type {
  DealerEntityAdminSpaceInfluence,
  DealerEntityAdminSpaceSupport,
  DealerSpaceInfluenceRecordByCustomerConfigurationField,
  DealerSpaceParameterizationInfluenceField,
} from './entityAdmin';
import { DealerSpaceParameterKey } from './types';

type CustomerConfigurationInfluenceFields = Partial<Record<DealerSpaceParameterizationInfluenceField, string>>;

function getSingleSelectedCustomer(value: unknown): string | undefined {
  if (!Array.isArray(value) || value.length !== 1) {
    return undefined;
  }
  const [customer] = value;
  return typeof customer === 'string' && customer !== '' ? customer : undefined;
}

export function getDealerSpaceParameterizationInfluence(
  customerConfiguration: CustomerConfigurationInfluenceFields | undefined,
  field: DealerSpaceParameterizationInfluenceField
): SpaceInfluence | undefined {
  const rowId = customerConfiguration?.[field];
  return rowId
    ? {
        type: SpaceInfluenceType.HighlightRow,
        rowId,
      }
    : undefined;
}

export function useDealerSpaceParameterizationInfluence(field: DealerSpaceParameterizationInfluenceField) {
  const context = useOptionalSpaceParameters();
  const selectedCustomer = context?.active
    ? getSingleSelectedCustomer(context.values[DealerSpaceParameterKey.Counterparty])
    : undefined;
  const query = useGetCustomerConfigurationQuery(selectedCustomer ?? skipToken);

  return useMemo(
    () => (selectedCustomer == null ? undefined : getDealerSpaceParameterizationInfluence(query.currentData, field)),
    [field, query.currentData, selectedCustomer]
  );
}

export function useApplyDealerSpaceParameterizationInfluence<TRecord extends EntityAdminRecord>(
  influence: SpaceInfluence | undefined
) {
  const [blotterTableApi, setBlotterTableApi] = useState<EntityAdminBlotterTableApi<TRecord>>();

  useApplySpaceInfluence({
    actOnInfluence: applyBlotterSpaceInfluence,
    blotterTableApi,
    cleanupInfluence: cleanupBlotterSpaceInfluence,
    influence,
    subscribeToInfluenceUpdates: subscribeToSpaceInfluenceUpdates,
  });

  return useCallback((api: EntityAdminBlotterTableApi<TRecord>) => {
    setBlotterTableApi(api);
  }, []);
}

export function useDealerInfluencedEntityAdminSpaceParameters<
  const TSupport extends DealerEntityAdminSpaceSupport<
    SpaceParameterPanelValueDefinitions,
    DealerEntityAdminSpaceInfluence
  >,
  const TFilterableProperties extends readonly (FilterableProperty | null | undefined)[] | undefined,
>(
  support: TSupport,
  filterableProperties: SpaceParameterCompatibleFilterableProperties<TSupport, TFilterableProperties>
) {
  type TRecord =
    DealerSpaceInfluenceRecordByCustomerConfigurationField[TSupport['influence']['customerConfigurationField']];

  const controlledFilter = useSpaceParameterControlledFilter(support, filterableProperties);
  const influence = useDealerSpaceParameterizationInfluence(support.influence.customerConfigurationField);
  const rowClassRules = useMemo(() => getSpaceInfluenceRowClassRules<TRecord>(influence), [influence]);
  const onBlotterTableApiReady = useApplyDealerSpaceParameterizationInfluence<TRecord>(influence);

  return {
    externallyControlledFilter: controlledFilter.externallyControlledFilter,
    filterableProperties: controlledFilter.filterableProperties,
    onBlotterTableApiReady,
    rowClassRules,
  };
}
